import React, { useState, useEffect, useRef } from 'react';
import SlideEditor, { SlideContent } from './components/SlideEditor';
import SlidePreview from './components/SlidePreview';
import claudeService from './services/ClaudeService';
import { FileWithContent } from './services/PresentationService';
import { AuthProvider } from './contexts/AuthContext';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login';
import SignUp from './components/SignUp';
import PrivateRoute from './components/PrivateRoute';
import UserProfile from './components/UserProfile';
import Dashboard from './components/Dashboard';
import PresentationView from './components/PresentationView';
import PresentationEditor from './components/PresentationEditor';
import presentationService from './services/PresentationService';

const SlideGeneratorApp: React.FC = () => {
  const [slideHtml, setSlideHtml] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [contextFiles, setContextFiles] = useState<FileWithContent[]>([]);
  const [animateIn, setAnimateIn] = useState(false);
  const appRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Trigger animations after component mounts
    setTimeout(() => setAnimateIn(true), 100);
  }, []);

  const handleGenerateSlides = async (content: SlideContent & { html?: string }, files: FileWithContent[]) => {
    setIsGenerating(true);
    setContextFiles(files);
    
    try {
      // Use the HTML passed from SlideEditor if available, otherwise generate it
      let htmlResponse = content.html;
      
      if (!htmlResponse) {
        // Only make the API call if we don't already have HTML
        htmlResponse = await claudeService.generateSlides(content, files);
      }
      
      // Extract HTML content from the response, which might be wrapped in markdown code blocks
      // or have explanatory text before/after the HTML
      let extractedHtml = htmlResponse;
      
      // Look for HTML content wrapped in markdown code blocks like ```html ... ```
      const htmlMarkdownMatch = htmlResponse.match(/```(?:html)?\s*([\s\S]*?)```/);
      if (htmlMarkdownMatch && htmlMarkdownMatch[1]) {
        extractedHtml = htmlMarkdownMatch[1].trim();
      } else {
        // If no markdown code block is found, try to find HTML between <html> tags
        const htmlTagMatch = htmlResponse.match(/<html[\s\S]*?>[\s\S]*?<\/html>/i);
        if (htmlTagMatch) {
          extractedHtml = htmlTagMatch[0];
        } else {
          // Try to find doctype or body tags
          const doctypeMatch = htmlResponse.match(/<!DOCTYPE[\s\S]*?>[\s\S]*/i);
          if (doctypeMatch) {
            extractedHtml = doctypeMatch[0];
          } else {
            const bodyMatch = htmlResponse.match(/<body[\s\S]*?>[\s\S]*?<\/body>/i);
            if (bodyMatch) {
              extractedHtml = `<html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head>${bodyMatch[0]}</html>`;
            }
          }
        }
      }
      
      // Safety measures - prevent JavaScript execution and unwanted component injection
      extractedHtml = extractedHtml
        // Remove script tags completely
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        // Remove onclick and other event handlers
        .replace(/\s+on\w+\s*=\s*["']?[^"'>\s]+/gi, '')
        // Remove javascript: URLs
        .replace(/\shref\s*=\s*["']?javascript:[^"'>\s]+/gi, ' href="#"')
        // Remove data URIs with javascript
        .replace(/\ssrc\s*=\s*["']?data:text\/(?:html|javascript)[^"'>\s]+/gi, ' src=""')
        // Prevent accidental component injection
        .replace(/slidemage\.pages\.dev\/presentation\/22IQ1xZcyiVyzOWTVA2q/g, 'example-placeholder');
      
      // Add metadata for print-friendly setup and fixed size
      if (!extractedHtml.includes('<meta name="viewport"')) {
        extractedHtml = extractedHtml.replace('</head>', 
          `<meta name="viewport" content="width=1920, height=1080, initial-scale=1.0">
          <style>
            @media print {
              .slide {
                page-break-after: always;
                width: 1920px;
                height: 1080px;
                overflow: hidden;
                position: relative;
              }
            }
            body {
              margin: 0;
              padding: 0;
            }
            .slide {
              width: 1920px;
              height: 1080px;
              overflow: hidden;
              position: relative;
              box-sizing: border-box;
            }
          </style>
          </head>`);
      }
      
      setSlideHtml(extractedHtml);
      
      // Save presentation to database
      try {
        await presentationService.createPresentation({
          title: content.title,
          content: content.content,
          html: extractedHtml, // Save the extracted HTML
          contextFiles: files
        });
        console.log('Presentation saved to Firestore');
      } catch (saveError) {
        console.error('Error saving presentation to Firestore:', saveError);
      }
    } catch (error) {
      console.error('Error generating slides:', error);
      alert('There was an error generating your slides. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div ref={appRef} className="relative min-h-screen">
      {/* Dynamic Background Elements */}
      <div className="fixed inset-0 z-0 overflow-hidden">
        {/* Noise Texture */}
        <div className="absolute inset-0 opacity-30 z-0 bg-noise"></div>
        
        {/* Gradient Orbs */}
        <div className="absolute top-[-30%] right-[-20%] w-[70%] h-[70%] rounded-full bg-purple-900/20 blur-3xl"></div>
        <div className="absolute bottom-[-10%] left-[-20%] w-[50%] h-[60%] rounded-full bg-teal-900/20 blur-3xl"></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        
        {/* Subtle animated flowing paths */}
        <svg className="absolute inset-0 w-full h-full z-0 opacity-10" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d="M0,50 Q25,30 50,50 T100,50 L100,100 L0,100 Z" fill="url(#gradient1)" className="animate-wave1"></path>
          <path d="M0,60 Q25,40 50,60 T100,60 L100,100 L0,100 Z" fill="url(#gradient2)" className="animate-wave2"></path>
          <defs>
            <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#7e22ce" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#0d9488" stopOpacity="0.3" />
            </linearGradient>
            <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#0d9488" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#7e22ce" stopOpacity="0.5" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Main Content */}
      <div className={`relative z-10 container mx-auto px-4 py-8 transition-all duration-1000 ease-out ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <main className="space-y-12">
          <Routes>
            <Route path="/dashboard" element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            } />
            <Route path="/presentation/:id" element={
              <PrivateRoute>
                <PresentationView />
              </PrivateRoute>
            } />
            <Route path="/edit/:id" element={
              <PrivateRoute>
                <PresentationEditor />
              </PrivateRoute>
            } />
            <Route path="/" element={
              <PrivateRoute>
                <div>
                  <SlideEditor onGenerateSlides={handleGenerateSlides} isLoading={isGenerating} />
                  
                  {slideHtml && (
                    <SlidePreview slideHtml={slideHtml} />
                  )}
                </div>
              </PrivateRoute>
            } />
          </Routes>
          
          {isGenerating && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="glassmorphism p-8 rounded-2xl max-w-md w-full border border-white/10 shadow-2xl">
                <div className="flex flex-col items-center text-center">
                  <div className="relative mb-6">
                    <div className="absolute -inset-7 bg-gradient-to-r from-violet-600 to-teal-600 rounded-full opacity-70 blur-lg animate-pulse"></div>
                    <div className="relative w-20 h-20 flex items-center justify-center rounded-full bg-gradient-to-r from-violet-800 to-violet-900 shadow-xl">
                      <svg className="w-8 h-8 text-teal-400 animate-bounce" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 2.75V7.25M16 2.75V7.25M3 10.25H21M7 14.75L9 16.75L12 13.75M15 14.75H15.01M8 21.25H16C18.4853 21.25 20.5 19.2353 20.5 16.75V8.75C20.5 6.26472 18.4853 4.25 16 4.25H8C5.51472 4.25 3.5 6.26472 3.5 8.75V16.75C3.5 19.2353 5.51472 21.25 8 21.25Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold mb-3">Creating Your Slides</h3>
                  <p className="text-white/70 mb-6">
                    Our AI is working on your presentation
                    {contextFiles.length > 0 && ` using ${contextFiles.length} context ${contextFiles.length === 1 ? 'file' : 'files'}`}
                  </p>
                  
                  <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden mb-4">
                    <div className="h-full bg-gradient-to-r from-violet-600 via-purple-600 to-teal-600 animate-shimmer"></div>
                  </div>
                  
                  <p className="text-sm text-white/50">This may take a minute or two...</p>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
      
      <style>{`
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        
        .animate-shimmer {
          background-size: 1000px 100%;
          animation: shimmer 2s infinite linear;
        }
        
        @keyframes wave1 {
          0%, 100% { d: path("M0,50 Q25,30 50,50 T100,50 L100,100 L0,100 Z"); }
          50% { d: path("M0,50 Q25,70 50,50 T100,50 L100,100 L0,100 Z"); }
        }
        
        @keyframes wave2 {
          0%, 100% { d: path("M0,60 Q25,40 50,60 T100,60 L100,100 L0,100 Z"); }
          50% { d: path("M0,60 Q25,80 50,60 T100,60 L100,100 L0,100 Z"); }
        }
        
        .animate-wave1 {
          animation: wave1 10s ease-in-out infinite;
        }
        
        .animate-wave2 {
          animation: wave2 13s ease-in-out infinite;
        }
        
        .bg-noise {
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 250 250' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
        }
  
        .bg-grid-pattern {
          background-image: linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
                           linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
          background-size: 20px 20px;
        }
        
        .glassmorphism {
          background: rgba(15, 23, 42, 0.6);
          backdrop-filter: blur(12px);
        }
      `}</style>
    </div>
  );
};

const Header: React.FC = () => {
  return (
    <header className="bg-[#0F172A]/90 backdrop-blur-md border-b border-white/10 text-white py-4 px-6 sticky top-0 z-50 shadow-lg shadow-violet-900/10">
      <div className="container mx-auto flex justify-between items-center">
        <a href="/" className="text-2xl font-bold flex items-center group">
          <div className="relative w-10 h-10 mr-3">
            <div className="absolute inset-0 rounded-lg bg-violet-600/50 blur-md group-hover:bg-violet-500/50 transition-all duration-300 animate-pulse"></div>
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-600 to-violet-800 flex items-center justify-center relative border border-violet-500/50 group-hover:from-violet-500 group-hover:to-violet-700 transition-all duration-300">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
              </svg>
            </div>
          </div>
          <div className="relative">
            <span>Slide<span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-300 to-teal-500 font-extrabold">Mage</span></span>
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-teal-400 to-teal-400/0 group-hover:w-full transition-all duration-300 rounded-full"></span>
          </div>
        </a>
        <div className="flex items-center space-x-3">
          <a href="/dashboard" className="group flex items-center px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-violet-500/30 transition-all duration-300">
            <svg className="w-5 h-5 mr-2 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
            </svg>
            <span className="text-sm">My Presentations</span>
          </a>
          <a href="/profile" className="relative group px-5 py-2 overflow-hidden rounded-lg bg-gradient-to-r from-violet-800/80 to-teal-800/80 hover:from-violet-700/80 hover:to-teal-700/80 border border-white/10 shadow-md transition-all duration-300 flex items-center">
            <div className="absolute inset-0 w-full h-full opacity-5" style={{
              backgroundImage: "url('data:image/svg+xml,%3Csvg xmlns=\"http://www.w3.org/2000/svg\" width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"%23fff\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"%3E%3Cpath d=\"M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z\"%3E%3C/path%3E%3C/svg%3E')",
              backgroundSize: "100px 100px"
            }}></div>
            <svg className="w-5 h-5 mr-2 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
            </svg>
            <span className="relative z-10 font-medium">My Profile</span>
          </a>
        </div>
      </div>
    </header>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="min-h-screen bg-[#0F172A] bg-noise flex flex-col">
          <Header />
          
          <main className="flex-grow">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignUp />} />
              <Route 
                path="/profile" 
                element={
                  <PrivateRoute>
                    <UserProfile />
                  </PrivateRoute>
                } 
              />
              <Route 
                path="/*" 
                element={
                  <PrivateRoute>
                    <SlideGeneratorApp />
                  </PrivateRoute>
                } 
              />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </main>
          
          <footer className="py-8 text-center text-gray-500 border-t border-white/5 relative z-10 mt-10">
            <div className="container mx-auto">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center">
                  <div className="relative w-8 h-8 mr-2">
                    <div className="absolute inset-0 rounded-lg bg-violet-600/30 blur-sm"></div>
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-800 to-violet-900 flex items-center justify-center relative">
                      <svg className="w-4 h-4 text-teal-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" stroke="currentColor"/>
                      </svg>
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-white/80">© {new Date().getFullYear()} <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-violet-500 font-bold">SlideMage</span></p>
                    <p className="text-xs text-gray-400">Powered by <span className="text-violet-400">Claude AI</span></p>
                  </div>
                </div>
                
                <div className="flex space-x-6">
                  <a href="/privacy" className="text-white/50 hover:text-teal-400 text-sm transition-colors duration-300 flex items-center">
                    <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M12 11V16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M12 8V8.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    Privacy
                  </a>
                  <a href="/terms" className="text-white/50 hover:text-teal-400 text-sm transition-colors duration-300 flex items-center">
                    <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 14L15 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M9 10L15 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M9 18L13 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H14.5858C14.851 3 15.1054 3.10536 15.2929 3.29289L20.7071 8.70711C20.8946 8.89464 21 9.149 21 9.41421V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M14 3V7C14 7.53043 14.2107 8.03914 14.5858 8.41421C14.9609 8.78929 15.4696 9 16 9H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                    Terms
                  </a>
                  <a href="/contact" className="text-white/50 hover:text-teal-400 text-sm transition-colors duration-300 flex items-center">
                    <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M11 8C11 7.44772 11.4477 7 12 7C12.5523 7 13 7.44772 13 8V12C13 12.5523 12.5523 13 12 13C11.4477 13 11 12.5523 11 12V8Z" fill="currentColor" />
                      <path d="M11 16C11 15.4477 11.4477 15 12 15C12.5523 15 13 15.4477 13 16C13 16.5523 12.5523 17 12 17C11.4477 17 11 16.5523 11 16Z" fill="currentColor" />
                    </svg>
                    Contact
                  </a>
                  <a href="https://github.com/yourusername/slidemage" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-teal-400 text-sm transition-colors duration-300 flex items-center">
                    <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C6.477 2 2 6.477 2 12C2 16.418 4.865 20.166 8.839 21.489C9.339 21.581 9.521 21.272 9.521 21.006C9.521 20.769 9.513 20.005 9.513 19.14C7 19.698 6.35 18.576 6.15 17.974C6.037 17.668 5.55 16.758 5.125 16.52C4.775 16.333 4.275 15.776 5.112 15.764C5.9 15.751 6.462 16.548 6.65 16.866C7.55 18.361 8.988 18.019 9.562 17.752C9.654 17.14 9.929 16.724 10.229 16.486C8.038 16.247 5.75 15.383 5.75 11.586C5.75 10.494 6.138 9.596 6.675 8.897C6.57 8.643 6.225 7.616 6.788 6.215C6.788 6.215 7.625 5.95 9.525 7.261C10.325 7.021 11.175 6.901 12.025 6.901C12.875 6.901 13.725 7.021 14.525 7.261C16.425 5.937 17.262 6.215 17.262 6.215C17.825 7.616 17.48 8.643 17.375 8.897C17.912 9.596 18.3 10.482 18.3 11.586C18.3 15.396 16 16.247 13.809 16.486C14.188 16.789 14.513 17.368 14.513 18.279C14.513 19.602 14.505 20.659 14.505 21.006C14.505 21.272 14.688 21.593 15.188 21.489C17.173 20.817 18.915 19.526 20.115 17.788C21.315 16.051 21.91 13.979 21.1 12C21.1 6.477 16.523 2 11 2Z" />
                    </svg>
                    GitHub
                  </a>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </Router>

      {/* Global styles */}
      <style>{`
        .glassmorphism {
          background: rgba(15, 23, 42, 0.6);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.05);
        }
        
        .bg-noise {
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.05'/%3E%3C/svg%3E");
          background-position: 0 0;
          background-size: 200px 200px;
        }
        
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        
        .animate-shimmer {
          animation: shimmer 8s infinite linear;
        }
        
        @keyframes wave1 {
          0%, 100% { d: path("M0,50 Q25,30 50,50 T100,50 L100,100 L0,100 Z"); }
          50% { d: path("M0,50 Q25,70 50,50 T100,50 L100,100 L0,100 Z"); }
        }
        
        @keyframes wave2 {
          0%, 100% { d: path("M0,60 Q25,40 50,60 T100,60 L100,100 L0,100 Z"); }
          50% { d: path("M0,60 Q25,80 50,60 T100,60 L100,100 L0,100 Z"); }
        }
        
        .animate-wave1 {
          animation: wave1 10s ease-in-out infinite;
        }
        
        .animate-wave2 {
          animation: wave2 13s ease-in-out infinite;
        }
      `}</style>
    </AuthProvider>
  );
}

export default App;
